import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAdminAuth } from '../contexts/AdminAuthContext';

// Tab components (will be created)
import ResumenTab from '../components/userDetail/ResumenTab';
import MatriculaPlanTab from '../components/userDetail/MatriculaPlanTab';
import PagosTab from '../components/userDetail/PagosTab';
import SesionesTab from '../components/userDetail/SesionesTab';
import ProgresoTab from '../components/userDetail/ProgresoTab';
import ComunidadTab from '../components/userDetail/ComunidadTab';
import SoporteTab from '../components/userDetail/SoporteTab';
import SeguridadTab from '../components/userDetail/SeguridadTab';
import AuditoriaTab from '../components/userDetail/AuditoriaTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'resumen', name: 'Resumen', icon: '📊' },
  { id: 'matricula', name: 'Matrícula & Plan', icon: '🎓' },
  { id: 'pagos', name: 'Pagos & Facturas', icon: '💳' },
  { id: 'sesiones', name: 'Sesiones en vivo', icon: '🎥' },
  { id: 'progreso', name: 'Progreso & Proyectos', icon: '📈' },
  { id: 'comunidad', name: 'Comunidad & Accesos', icon: '👥' },
  { id: 'soporte', name: 'Soporte & Actividad', icon: '🎫' },
  { id: 'seguridad', name: 'Seguridad & Permisos', icon: '🔒' },
  { id: 'auditoria', name: 'Historial & Auditoría', icon: '📋' },
];

export default function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAdminAuth();
  
  const [activeTab, setActiveTab] = useState('resumen');
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showChangePhotoModal, setShowChangePhotoModal] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  const fetchUserData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/users/${userId}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      setUserData(response.data);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(err.response?.data?.detail || 'Error al cargar datos del usuario');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshData = () => {
    fetchUserData();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#D4AF37] mx-auto"></div>
          <p className="mt-4 text-gray-400">Cargando datos del usuario...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-6">
          <h3 className="text-red-400 font-semibold mb-2">Error</h3>
          <p className="text-red-300">{error}</p>
          <button
            onClick={() => navigate('/admin/users')}
            className="mt-4 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
          >
            Volver a Usuarios
          </button>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <p className="text-gray-400">Usuario no encontrado</p>
      </div>
    );
  }

  const { user, enrollment, summary } = userData;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="text-gray-400 hover:text-white transition-colors"
        >
          Admin
        </button>
        <span className="text-gray-600">/</span>
        <button
          onClick={() => navigate('/admin/users')}
          className="text-gray-400 hover:text-white transition-colors"
        >
          Usuarios
        </button>
        <span className="text-gray-600">/</span>
        <span className="text-white">{user.full_name || user.email}</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button
            onClick={() => setShowChangePhotoModal(true)}
            className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4E4B7] flex items-center justify-center text-black text-2xl font-bold hover:opacity-80 transition-opacity relative group"
            title="Click para cambiar foto"
          >
            {user.profile_picture_url ? (
              <img 
                src={user.profile_picture_url} 
                alt={user.full_name} 
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              user.full_name?.charAt(0).toUpperCase()
            )}
            <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
          </button>
          
          <div>
            <h1 className="text-3xl font-bold text-white">{user.full_name}</h1>
            <p className="text-gray-400">{user.email}</p>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-xs text-gray-500">ID: {user.id}</span>
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                  summary.enrollment_status === 'active'
                    ? 'bg-green-500/10 text-green-400 border-green-500/30'
                    : summary.enrollment_status === 'suspended'
                    ? 'bg-red-500/10 text-red-400 border-red-500/30'
                    : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'
                }`}
              >
                {summary.enrollment_status}
              </span>
              {enrollment && enrollment.cohort_id && (
                <span className="text-xs text-gray-400 px-2 py-1 bg-[#1a4d5c]/20 rounded border border-[#1a4d5c]/30">
                  📚 Cohorte: {enrollment.cohort_id}
                </span>
              )}
              {summary.has_scholarship && (
                <span className="text-xs text-[#D4AF37] px-2 py-1 bg-[#D4AF37]/10 rounded border border-[#D4AF37]/30 font-semibold">
                  🎓 Beca {summary.scholarship_percentage}%
                </span>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={refreshData}
          className="px-4 py-2 bg-[#1a4d5c]/30 hover:bg-[#1a4d5c]/50 rounded-lg text-white transition-colors border border-[#1a4d5c]/50 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Actualizar
        </button>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-[#0A0A0A] border border-[#1a4d5c]/30 rounded-lg overflow-hidden">
        <div className="flex overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 px-6 py-4 text-sm font-medium transition-all border-b-2 ${
                activeTab === tab.id
                  ? 'border-[#D4AF37] text-[#D4AF37] bg-[#1a4d5c]/10'
                  : 'border-transparent text-gray-400 hover:text-white hover:bg-[#1a4d5c]/5'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-[#0A0A0A] border border-[#1a4d5c]/30 rounded-lg p-6">
        {activeTab === 'resumen' && <ResumenTab userData={userData} refreshData={refreshData} />}
        {activeTab === 'matricula' && <MatriculaPlanTab userData={userData} refreshData={refreshData} />}
        {activeTab === 'pagos' && <PagosTab userData={userData} refreshData={refreshData} />}
        {activeTab === 'sesiones' && <SesionesTab userData={userData} refreshData={refreshData} />}
        {activeTab === 'progreso' && <ProgresoTab userData={userData} refreshData={refreshData} />}
        {activeTab === 'comunidad' && <ComunidadTab userData={userData} refreshData={refreshData} />}
        {activeTab === 'soporte' && <SoporteTab userData={userData} refreshData={refreshData} />}
        {activeTab === 'seguridad' && <SeguridadTab userData={userData} refreshData={refreshData} />}
        {activeTab === 'auditoria' && <AuditoriaTab userData={userData} refreshData={refreshData} />}
      </div>

      {/* Change Photo Modal */}
      {showChangePhotoModal && (
        <ChangePhotoModal
          userId={user.id}
          currentPhoto={user.profile_picture_url}
          onClose={() => setShowChangePhotoModal(false)}
          onSuccess={() => {
            setShowChangePhotoModal(false);
            fetchUserData();
          }}
        />
      )}
    </div>
  );
}

// Change Photo Modal Component
function ChangePhotoModal({ userId, currentPhoto, onClose, onSuccess }) {
  const { getToken } = useAdminAuth();
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [preview, setPreview] = React.useState(currentPhoto);
  const [isUploading, setIsUploading] = React.useState(false);
  const [uploadMethod, setUploadMethod] = React.useState('file');
  const [imageUrl, setImageUrl] = React.useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('El archivo es muy grande. Máximo 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        alert('Solo se permiten imágenes');
        return;
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (uploadMethod === 'file' && !selectedFile) {
      alert('Por favor selecciona una imagen');
      return;
    }

    if (uploadMethod === 'url' && !imageUrl.trim()) {
      alert('Por favor ingresa una URL de imagen');
      return;
    }

    setIsUploading(true);
    try {
      if (uploadMethod === 'file') {
        const formData = new FormData();
        formData.append('file', selectedFile);
        
        await axios.post(
          `${BACKEND_URL}/api/admin/users/${userId}/profile-picture`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } else {
        await axios.patch(
          `${BACKEND_URL}/api/admin/users/${userId}`,
          { profile_picture_url: imageUrl },
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
      }
      
      alert('Foto actualizada correctamente');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al subir la foto');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-[#0A0A0A] rounded-lg max-w-md w-full p-6 border border-[#1a4d5c]/30">
        <h3 className="text-xl font-bold text-white mb-4">Cambiar Foto de Perfil</h3>
        
        <div className="space-y-4">
          <div className="flex justify-center mb-4">
            <div className="relative w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-[#D4AF37] to-[#F4E4B7] flex items-center justify-center">
              {preview ? (
                <img src={preview} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <span className="text-black text-4xl font-bold">?</span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">Método</label>
            <div className="flex gap-2">
              <button
                onClick={() => setUploadMethod('file')}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                  uploadMethod === 'file'
                    ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/50'
                    : 'bg-black/50 text-gray-400 border border-[#1a4d5c]/50'
                }`}
              >
                📁 Subir Archivo
              </button>
              <button
                onClick={() => setUploadMethod('url')}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                  uploadMethod === 'url'
                    ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/50'
                    : 'bg-black/50 text-gray-400 border border-[#1a4d5c]/50'
                }`}
              >
                🔗 URL
              </button>
            </div>
          </div>

          {uploadMethod === 'file' && (
            <div>
              <label className="block text-sm text-gray-300 mb-2">Seleccionar Imagen</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#D4AF37]/20 file:text-[#D4AF37] hover:file:bg-[#D4AF37]/30 file:cursor-pointer"
              />
              <p className="text-xs text-gray-500 mt-1">Máximo 5MB. Formatos: JPG, PNG, GIF</p>
            </div>
          )}

          {uploadMethod === 'url' && (
            <div>
              <label className="block text-sm text-gray-300 mb-2">URL de la Imagen</label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => {
                  setImageUrl(e.target.value);
                  setPreview(e.target.value);
                }}
                placeholder="https://ejemplo.com/imagen.jpg"
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              onClick={handleUpload}
              disabled={isUploading || (uploadMethod === 'file' && !selectedFile) || (uploadMethod === 'url' && !imageUrl)}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black rounded-lg hover:from-[#F4E4B7] hover:to-[#D4AF37] disabled:opacity-50"
            >
              {isUploading ? 'Subiendo...' : 'Guardar Foto'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
